/**
 * MobileSpaceView Component
 * 
 * Unified mobile view that combines canvas + voice interaction.
 * - Block list with drag-to-reorder
 * - MorphingPill pinned at bottom with AmbientWave
 * - Talk overlay slides up when chat tapped
 * - Vision capture ref for AI context
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DndContext, closestCenter, DragEndEvent, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import MobileBlockCard from './MobileBlockCard';
import MobileBlockFullscreen from './MobileBlockFullscreen';
import MobileInsertSheet from './MobileInsertSheet';
import MobileTalkOverlay from './MobileTalkOverlay';
import MobileBlockRenderer from './MobileBlockRenderer';
import MobileWelcome from './MobileWelcome';
import MobileTodoSheet from './MobileTodoSheet';
import MorphingPill from '@/components/chat/MorphingPill';
import AmbientWave, { type AmbientState } from '@/components/chat/AmbientWave';
import type { GeminiAudioManager } from '@/lib/gemini-audio';
import type { TranscriptMessage } from '@/components/chat/TranscriptPanel';
import type { TodoItem } from '@/types/journal';
import { analyzeYouTube, generateQuiz } from '@/services/journalAIService';
import './mobile.css';

export interface MobileBlock {
    id: string;
    type: 'note' | 'pdf' | 'youtube' | 'quiz' | 'slide-deck';
    title: string;
    subtitle?: string;
    preview?: string; // For notes: first few lines of content
    content?: unknown;
    isExpanded?: boolean;
}

interface MobileSpaceViewProps {
    // Journal data
    journalId: string;
    journalName: string;
    blocks: MobileBlock[];

    // Vision capture
    onCanvasViewportRef?: (getEl: () => HTMLElement | null) => void;

    // Agent state
    agentStatus: 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';
    ambientState: AmbientState;
    isUserSpeaking?: boolean;
    isAgentSpeaking?: boolean;
    isUserMuted?: boolean;
    audioManager?: GeminiAudioManager | null;

    // Transcript
    messages: TranscriptMessage[];

    // Actions
    onConnect: () => void;
    onDisconnect: () => void;
    onToggleMute: () => void;
    onSendText?: (text: string) => void;

    // Block actions
    onBlockClick?: (blockId: string) => void;
    onAddBlock?: (type: string, config?: { pdfFile?: File; youtubeUrl?: string; quizTopic?: string; quizCount?: number; quizDifficulty?: 'easy' | 'medium' | 'hard'; explainerTopic?: string }) => void;
    onReorderBlocks?: (blocks: MobileBlock[]) => void;
    onDeleteBlock?: (blockId: string) => void;
    onContentChange?: (blockId: string, content: unknown) => void;

    // Todos
    todos?: TodoItem[];
    onTodoToggle?: (id: string) => void;
    onTodoAdd?: (text: string) => void;
    onTodoDelete?: (id: string) => void;

    // Navigation
    onMenuOpen?: () => void;
    onBack?: () => void;

    // Auto-expand a block (for auto-opening newly uploaded PDFs)
    autoExpandBlockId?: string | null;

    // Onboarding mode (first-time user experience)
    isOnboardingMode?: boolean;
}

const MobileSpaceView: React.FC<MobileSpaceViewProps> = ({
    journalId,
    journalName,
    blocks,
    onCanvasViewportRef,
    agentStatus,
    ambientState,
    isUserSpeaking = false,
    isAgentSpeaking = false,
    isUserMuted = true,
    audioManager,
    messages,
    onConnect,
    onDisconnect,
    onToggleMute,
    onSendText,
    onBlockClick,
    onAddBlock,
    onReorderBlocks,
    onDeleteBlock,
    onContentChange,
    todos = [],
    onTodoToggle,
    onTodoAdd,
    onTodoDelete,
    onMenuOpen,
    onBack,
    autoExpandBlockId,
    isOnboardingMode = false,
}) => {
    // State
    const [showTalkOverlay, setShowTalkOverlay] = useState(false);
    const [showInsertSheet, setShowInsertSheet] = useState(false);
    const [showTodoSheet, setShowTodoSheet] = useState(false);
    const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);

    // Local blocks state for immediate drag-drop feedback
    const [localBlocks, setLocalBlocks] = useState(blocks);

    // Sync local blocks when props change (e.g., after database refresh)
    React.useEffect(() => {
        setLocalBlocks(blocks);
    }, [blocks]);

    // Auto-expand block when autoExpandBlockId changes
    React.useEffect(() => {
        if (autoExpandBlockId) {
            setExpandedBlockId(autoExpandBlockId);
        }
    }, [autoExpandBlockId]);

    // Refs
    const canvasRef = useRef<HTMLDivElement>(null);

    // Vision capture - expose canvas element
    React.useEffect(() => {
        if (onCanvasViewportRef) {
            onCanvasViewportRef(() => canvasRef.current);
        }
    }, [onCanvasViewportRef]);

    // ===== BACKEND INTEGRATION HOOKS (matching SpaceCanvasSection patterns) =====

    // Track YouTube blocks currently being analyzed to prevent duplicate calls
    const analyzingYouTubeBlocksRef = useRef<Set<string>>(new Set());

    // Auto-trigger YouTube analysis for blocks with analyzing: true
    useEffect(() => {
        localBlocks.forEach(async (block) => {
            if (block.type !== 'youtube') return;

            const content = block.content as {
                videoUrl?: string;
                analyzing?: boolean;
                transcript?: unknown[];
                analysis?: unknown;
                error?: string;
            } | null;

            // Skip if no content or no videoUrl
            if (!content?.videoUrl) return;

            // Skip if not marked as needing analysis
            if (!content.analyzing) return;

            // Skip if already has analysis data or error
            if (content.transcript || content.analysis || content.error) return;

            // Skip if we're already analyzing this block
            if (analyzingYouTubeBlocksRef.current.has(block.id)) return;

            // Mark as in-progress
            analyzingYouTubeBlocksRef.current.add(block.id);

            console.log(`[YouTube Mobile] Auto-triggering analysis for block ${block.id}`);

            try {
                const analysis = await analyzeYouTube({
                    journal_id: journalId,
                    video_url: content.videoUrl,
                    generate_summary: true,
                });

                console.log('[YouTube Mobile] Analysis complete:', analysis);

                // Update block with analysis results
                const contentWithAnalysis = {
                    videoUrl: content.videoUrl,
                    analyzing: false,
                    video_id: analysis.video_id,
                    transcript_available: analysis.transcript_available,
                    transcript_length: analysis.transcript_length,
                    transcript: analysis.transcript,
                    analysis: analysis.analysis,
                };

                onContentChange?.(block.id, contentWithAnalysis);

            } catch (error) {
                console.error('[YouTube Mobile] Analysis failed:', error);
                onContentChange?.(block.id, { ...content, analyzing: false, error: 'Analysis failed' });
            } finally {
                analyzingYouTubeBlocksRef.current.delete(block.id);
            }
        });
    }, [localBlocks, journalId, onContentChange]);

    // Track quiz blocks currently being generated to prevent duplicate calls
    const generatingQuizBlocksRef = useRef<Set<string>>(new Set());

    // Auto-trigger quiz generation for blocks with generating: true
    useEffect(() => {
        localBlocks.forEach(async (block) => {
            if (block.type !== 'quiz') return;

            const content = block.content as {
                generating?: boolean;
                prompt?: string;
                quizType?: string;
                questionCount?: number;
                difficulty?: string;
                generatedQuiz?: unknown;
                error?: string;
            } | null;

            // Skip if no content or not marked for generation
            if (!content?.generating) return;

            // Skip if already has quiz data or error
            if (content.generatedQuiz || content.error) return;

            // Skip if we're already generating this block
            if (generatingQuizBlocksRef.current.has(block.id)) return;

            // Need a prompt/topic
            if (!content.prompt) return;

            // Mark as in-progress
            generatingQuizBlocksRef.current.add(block.id);

            console.log(`[Quiz Mobile] Auto-triggering generation for block ${block.id}`);

            try {
                const response = await generateQuiz({
                    journal_id: journalId,
                    topic: content.prompt,
                    quiz_type: (content.quizType || 'mcq') as 'mcq' | 'true_false' | 'fill_blank' | 'matching' | 'spot_mistake',
                    question_count: content.questionCount || 5,
                    difficulty: (content.difficulty || 'medium') as 'easy' | 'medium' | 'hard',
                });

                console.log('[Quiz Mobile] Generation complete:', response);

                // Build updated content with generated quiz
                const contentWithQuiz = {
                    prompt: content.prompt,
                    quizType: content.quizType || 'mcq',
                    questionCount: content.questionCount || 5,
                    difficulty: content.difficulty || 'medium',
                    generating: false,
                    generatedQuiz: response.quiz,
                    answers: {},
                };

                onContentChange?.(block.id, contentWithQuiz);

            } catch (error) {
                console.error('[Quiz Mobile] Generation failed:', error);
                onContentChange?.(block.id, { ...content, generating: false, error: 'Quiz generation failed' });
            } finally {
                generatingQuizBlocksRef.current.delete(block.id);
            }
        });
    }, [localBlocks, journalId, onContentChange]);

    // Track explainer blocks currently being generated to prevent duplicate calls
    const generatingExplainerBlocksRef = useRef<Set<string>>(new Set());

    // Auto-trigger explainer generation for blocks with generating: true
    // Mobile UX: Auto-accept outline and go straight to generation (no approval step)
    useEffect(() => {
        localBlocks.forEach(async (block) => {
            if (block.type !== 'slide-deck') return;

            const content = block.content as {
                generating?: boolean;
                topic?: string;
                duration?: number;
                generatedSlides?: unknown[];
                state?: string;
                error?: string;
            } | null;

            // Skip if no content or not marked for generation
            if (!content?.generating) return;

            // Skip if already has slides or error
            if (content.generatedSlides?.length || content.error) return;

            // Skip if no topic
            if (!content.topic) return;

            // Skip if we're already generating this block
            if (generatingExplainerBlocksRef.current.has(block.id)) return;

            // Mark as in-progress
            generatingExplainerBlocksRef.current.add(block.id);

            console.log(`[Explainer Mobile] Auto-triggering generation for block ${block.id}`);

            try {
                // Dynamic import to avoid bundling issues
                const { generateOutline, generateSlidesFastestFirst } = await import('@/components/spaces/services/explainerService');

                // Step 1: Generate outline (auto-accepted on mobile)
                const outline = await generateOutline(content.topic, content.duration || 10, journalId);
                console.log('[Explainer Mobile] Outline generated (auto-accepted):', outline.title);

                // Step 2: Create skeleton slides with "generating" status
                const skeletonSlides = outline.slides.map((item: { id: string; title: string; description: string }, i: number) => ({
                    id: item.id,
                    order: i + 1,
                    title: i === 0 ? outline.title : item.title,
                    layout: i === 0 ? 'title' : 'text-only',
                    status: i === 0 ? 'ready' : 'generating',
                    content: i === 0
                        ? { heading: outline.title, subheading: outline.description }
                        : { heading: item.title, body: item.description },
                }));

                // Show skeleton slides immediately - user can see progress
                let currentSlides = [...skeletonSlides];
                onContentChange?.(block.id, {
                    ...content,
                    generating: false, // No longer in initial "generating" state
                    state: 'ready',
                    outline,
                    generatedSlides: currentSlides,
                    currentSlideIndex: 0,
                });

                // Step 3: Generate remaining slides and update as each completes
                for await (const { slide, index } of generateSlidesFastestFirst(outline, journalId, 5)) {
                    if (index === 0) continue; // Skip title slide

                    // Update the slide with full content (cast to any to avoid complex type inference)
                    currentSlides = currentSlides.map((s, i) =>
                        i === index ? { ...slide, status: 'ready' } as typeof s : s
                    );

                    // Update block content with new slide
                    onContentChange?.(block.id, {
                        ...content,
                        generating: false,
                        state: 'ready',
                        outline,
                        generatedSlides: currentSlides,
                        currentSlideIndex: 0,
                    });

                    console.log(`[Explainer Mobile] Slide ${index + 1}/${outline.slides.length} ready`);
                }

                console.log('[Explainer Mobile] All slides generated');

            } catch (error) {
                console.error('[Explainer Mobile] Generation failed:', error);
                onContentChange?.(block.id, { ...content, generating: false, error: 'Slide generation failed' });
            } finally {
                generatingExplainerBlocksRef.current.delete(block.id);
            }
        });
    }, [localBlocks, journalId, onContentChange]);

    // ===== END BACKEND INTEGRATION HOOKS =====

    // DnD sensors for touch
    const sensors = useSensors(
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    // Handle drag end - update local state immediately, then persist
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setLocalBlocks(prev => {
                const oldIndex = prev.findIndex(b => b.id === active.id);
                const newIndex = prev.findIndex(b => b.id === over.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                    const reordered = arrayMove(prev, oldIndex, newIndex);
                    // Persist to database
                    onReorderBlocks?.(reordered);
                    return reordered;
                }
                return prev;
            });
        }
    }, [onReorderBlocks]);

    // Handle block expansion
    const handleBlockClick = useCallback((blockId: string) => {
        setExpandedBlockId(blockId);
        onBlockClick?.(blockId);
    }, [onBlockClick]);

    // Handle block close
    const handleCloseBlock = useCallback(() => {
        setExpandedBlockId(null);
    }, []);

    // Toggle talk overlay
    const handleToggleTalk = useCallback(() => {
        setShowTalkOverlay(prev => !prev);
    }, []);

    // Get expanded block
    const expandedBlock = expandedBlockId
        ? blocks.find(b => b.id === expandedBlockId)
        : null;

    return (
        <div className="mobile-space-view">
            {/* Navigation Header - compact with todo */}
            <header className="mobile-nav-header">
                <button
                    className="mobile-nav-btn"
                    onClick={onMenuOpen || onBack}
                    aria-label={onMenuOpen ? "Open menu" : "Go back"}
                >
                    {onMenuOpen ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="3" y1="12" x2="21" y2="12" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    )}
                </button>
                <div
                    className="mobile-nav-title"
                    onClick={() => setShowTodoSheet(true)}
                    role="button"
                    tabIndex={0}
                >
                    <h1 className="mobile-nav-title-text">{journalName}</h1>
                    {(() => {
                        const remaining = todos.filter(t => !t.completed).length;
                        const total = todos.length;

                        if (remaining > 0) {
                            return (
                                <span className="mobile-nav-todo-badge remaining">
                                    {/* Circle progress icon */}
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <circle cx="12" cy="12" r="9" opacity="0.3" />
                                        <path d="M12 3a9 9 0 0 1 9 9" />
                                    </svg>
                                    {remaining} of {total} remaining
                                </span>
                            );
                        } else if (total > 0) {
                            return (
                                <span className="mobile-nav-todo-badge complete">
                                    {/* Check circle icon */}
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M9 12l2 2 4-4" />
                                    </svg>
                                    All done!
                                </span>
                            );
                        } else {
                            return (
                                <span className="mobile-nav-todo-badge empty">
                                    {/* Plus circle icon */}
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <circle cx="12" cy="12" r="9" />
                                        <line x1="12" y1="8" x2="12" y2="16" />
                                        <line x1="8" y1="12" x2="16" y2="12" />
                                    </svg>
                                    Add tasks
                                </span>
                            );
                        }
                    })()}
                </div>
                <div className="mobile-nav-spacer" />
            </header>

            {/* Vision Capture Wrapper - captures everything the user sees */}
            <div
                ref={canvasRef}
                className="mobile-vision-wrapper"
            >
                {/* Canvas Area - Scrollable block list */}
                <div className="mobile-space-canvas">
                    {/* Block List */}
                    {localBlocks.length === 0 ? (
                        <MobileWelcome
                            journalName={journalName}
                            onTapToStart={onConnect}
                            onAddBlock={() => setShowInsertSheet(true)}
                            isOnboardingMode={isOnboardingMode}
                            variant="canvas"
                        />
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={localBlocks.map(b => b.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="mobile-space-blocks">
                                    {localBlocks.map((block) => (
                                        <MobileBlockCard
                                            key={block.id}
                                            id={block.id}
                                            type={block.type}
                                            title={block.title}
                                            subtitle={block.subtitle}
                                            preview={block.preview}
                                            content={block.content}
                                            onClick={handleBlockClick}
                                            onDelete={onDeleteBlock}
                                            draggable
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                {/* Fullscreen Block - when expanded (INSIDE vision wrapper) */}
                {expandedBlock && (
                    <MobileBlockFullscreen
                        isOpen={true}
                        onClose={handleCloseBlock}
                        title={expandedBlock.title}
                        type={expandedBlock.type}
                    >
                        <MobileBlockRenderer
                            blockId={expandedBlock.id}
                            blockType={expandedBlock.type}
                            content={expandedBlock.content}
                            onContentChange={(content) => onContentChange?.(expandedBlock.id, content)}
                        />
                    </MobileBlockFullscreen>
                )}

                {/* Talk Overlay - slides up with transcript input */}
                <MobileTalkOverlay
                    isOpen={showTalkOverlay}
                    onClose={() => setShowTalkOverlay(false)}
                    messages={messages}
                    onSendText={onSendText}
                    agentStatus={agentStatus}
                    isUserSpeaking={isUserSpeaking}
                    isAgentSpeaking={isAgentSpeaking}
                    isUserMuted={isUserMuted}
                    onToggleMute={onToggleMute}
                    onConnect={onConnect}
                    onDisconnect={onDisconnect}
                />

                {/* Insert Sheet (INSIDE vision wrapper) */}
                <MobileInsertSheet
                    isOpen={showInsertSheet}
                    onClose={() => setShowInsertSheet(false)}
                    onSelectType={(type, config) => {
                        onAddBlock?.(type, config);
                        setShowInsertSheet(false);
                    }}
                />
            </div>

            {/* Agent Dock - Always visible except when talk/insert overlays are open */}
            {!showTalkOverlay && !showInsertSheet && (
                <div className="mobile-agent-dock">
                    {/* Ambient Wave - behind dock */}
                    <div className="mobile-dock-wave">
                        <AmbientWave state={ambientState} audioManager={audioManager} />
                    </div>

                    {/* Dock Content - Chat FAB (left) + Pill (center) + Insert FAB (right) */}
                    <div className="mobile-dock-content">
                        {/* Chat FAB - left side (only when connected) */}
                        {agentStatus === 'connected' && (
                            <button
                                className="mobile-dock-fab"
                                onClick={handleToggleTalk}
                                aria-label="Open chat"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                            </button>
                        )}

                        {/* MorphingPill - Dynamic Island style */}
                        <div className={`mobile-dock-pill ${agentStatus === 'connected' ? 'connected' : ''}`}>
                            <MorphingPill
                                agentStatus={agentStatus}
                                isUserSpeaking={isUserSpeaking}
                                isAgentSpeaking={isAgentSpeaking}
                                isUserMuted={isUserMuted}
                                onConnect={onConnect}
                                onDisconnect={onDisconnect}
                                onToggleMute={onToggleMute}
                                onToggleTranscript={handleToggleTalk}
                                isTranscriptVisible={showTalkOverlay}
                                showTranscriptToggle={false}
                                showBackground={false}
                                showDisconnectedLabel={true}
                                useMobileControlLayout={true}
                            />
                        </div>

                        {/* Contextual Action Button - right side */}
                        {expandedBlock ? (
                            // Three-dots menu when viewing a block
                            <div className="mobile-dock-menu-wrapper">
                                <button
                                    className="mobile-dock-fab"
                                    onClick={() => {
                                        const menu = document.getElementById('mobile-block-menu');
                                        if (menu) menu.classList.toggle('open');
                                    }}
                                    aria-label="Block options"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <circle cx="12" cy="6" r="2" />
                                        <circle cx="12" cy="12" r="2" />
                                        <circle cx="12" cy="18" r="2" />
                                    </svg>
                                </button>
                                {/* Popover Menu */}
                                <div id="mobile-block-menu" className="mobile-dock-popover">
                                    <button
                                        className="mobile-dock-popover-item"
                                        onClick={() => {
                                            handleCloseBlock();
                                            document.getElementById('mobile-block-menu')?.classList.remove('open');
                                        }}
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                        Back
                                    </button>
                                    <button
                                        className="mobile-dock-popover-item danger"
                                        onClick={() => {
                                            if (expandedBlock && onDeleteBlock) {
                                                onDeleteBlock(expandedBlock.id);
                                                handleCloseBlock();
                                            }
                                            document.getElementById('mobile-block-menu')?.classList.remove('open');
                                        }}
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // Insert FAB on canvas
                            <button
                                className="mobile-dock-fab"
                                onClick={() => setShowInsertSheet(true)}
                                aria-label="Add block"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Todo Sheet - slides up from bottom */}
            <MobileTodoSheet
                isOpen={showTodoSheet}
                onClose={() => setShowTodoSheet(false)}
                todos={todos}
                onToggle={onTodoToggle || (() => { })}
                onAdd={onTodoAdd || (() => { })}
                onDelete={onTodoDelete || (() => { })}
            />
        </div>
    );
};

export default MobileSpaceView;
